import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AdminFormattedMessages from '../../messages/admin/AdminFormattedMessages';
import Layout from '../../components/layouts/Layout';
import {
  Stack,
  Typography,
  Grid,
  Link,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { AdminRoutes } from '../../routes/AdminRoutes';
import { useLocation } from 'react-router-dom';

import useComplianceRequest from '../../requests/ComplianceRequest';

const CampaignViewPage = ({
  errorHandler,
  accessLevel,
  accessType,
  navigate,
}) => {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [enums, setEnums] = useState({});
  const [edits, setEdits] = useState({});

  // True if viewing a campaign, false if creating
  const [isEditing, setIsEditing] = useState(id ? true : false);
  const [loading, setLoading] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);

  const { getCampaign, getOptions, createCampaign, updateCampaign } =
    useComplianceRequest();

  // TODO: Change this so the brandCode is included in the URL
  const { brandCode } = useLocation()?.state || {};

  console.log('brandCode:', brandCode);

  const fetchCampaign = async (campaignId) => {
    // Handles redirect from edit page back to add page to instead go one page further back
    if (!campaignId) return navigate(-1);

    setCampaign(null);
    const data = await getCampaign(campaignId, errorHandler);
    console.log('campaign:', data);

    // If the campaign is pending, we need to resubmit it
    setIsResubmitting(data?.campaign_status === 'PENDING');
    setCampaign(data);
  };

  useEffect(() => {
    const loadEnums = async () => {
      const enums = await getOptions(['usecase', 'vertical'], errorHandler);
      console.log('enums:', enums);
      setEnums(enums);
    };

    loadEnums();

    // If form has gone from "add campaign" to "edit campaign"
    if (id && !isEditing) {
      setIsEditing(true);
      fetchCampaign(id);
    }

    if (isEditing) fetchCampaign(id);
  }, [id]);

  const handleFormChange = (event) => {
    let target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    let name = target.name;

    console.log('edits', edits);

    setEdits({
      ...edits,
      [name]: value,
    });
  };

  const handleSubUsecaseFormChange = (event) => {
    let target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    let name = target.name;

    console.log('edits', edits);

    setEdits({
      ...edits,
      subUsecases: {
        ...edits.subUsecases,
        [name]: value,
      },
    });
  };

  const submitForm = async () => {
    console.log('edits', edits);
    console.log(isEditing);

    setLoading(true);
    if (isEditing) {
      // Update campaign
      const updateData = {
        edits,
        isResubmitting, // PENDING campaigns are in the DB but not in TCR
      };
      const response = await updateCampaign(id, updateData, errorHandler);
      console.log('response', response);
      setEdits({});
      fetchCampaign();
    } else {
      // Create campaign
      const campaignData = {
        campaign: edits,
        brandCode,
      };
      const response = await createCampaign(campaignData, errorHandler);
      console.log('response', response);

      if (response?.campaign_id) {
        navigate(`/admin/compliance/campaign/${response?.campaign_id}`);
      }
    }
    setLoading(false);
  };

  const generateTextField = (
    label,
    field_name,
    isRequired,
    marginTop,
    isDisabled
  ) => {
    return (
      <Grid size={12}>
        <InputLabel
          sx={{
            width: 220,
            marginTop: marginTop || 0,
          }}
          required={isRequired}
        >
          {label}
        </InputLabel>
        <TextField
          multiline
          disabled={isDisabled}
          key={field_name}
          name={field_name}
          defaultValue={campaign?.[field_name] || ''}
          onChange={handleFormChange}
          size="small"
          sx={{ width: 340 }}
        />
      </Grid>
    );
  };

  const generateCheckbox = (label, name, isSubUsecase, notEditable) => {
    const editValue = isSubUsecase ? edits.subUsecases?.[name] : edits?.[name];
    const currentValue = isSubUsecase
      ? campaign?.subUsecases?.[name]
      : campaign?.[name];
    const newEditValue = editValue !== undefined ? editValue : currentValue;
    const disableEditing = isEditing && !isResubmitting;

    return (
      <Grid size={12} sx={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox
          name={name}
          key={`checkbox-${name}`}
          checked={newEditValue || false}
          disabled={disableEditing && (isSubUsecase || notEditable)}
          onChange={
            isSubUsecase ? handleSubUsecaseFormChange : handleFormChange
          }
          size="small"
        />
        {label}
      </Grid>
    );
  };

  const generateVerticalDropdown = () => {
    if (!enums || !enums?.vertical) {
      return null;
    }

    const verticals = Object.keys(enums?.vertical)?.map((item, index) => (
      <MenuItem key={index} value={item}>
        {item}
      </MenuItem>
    ));

    return (
      <Select
        name="vertical"
        value={edits?.vertical || campaign?.vertical || ''}
        onChange={handleFormChange}
        size="small"
        sx={{ width: 240, mb: 2 }}
        displayEmpty
      >
        {verticals}
      </Select>
    );
  };

  const generateUsecaseDropdown = () => {
    if (!enums || !enums?.usecase) {
      return null;
    }

    const usecases = Object.keys(enums?.usecase)?.map((item, index) => (
      <MenuItem key={index} value={item}>
        {item}
      </MenuItem>
    ));

    return (
      <Select
        name="usecase"
        value={edits?.usecase || campaign?.use_case || ''}
        onChange={handleFormChange}
        size="small"
        sx={{ width: 240, mb: 2 }}
        displayEmpty
        disabled={isEditing && !isResubmitting}
      >
        {usecases}
      </Select>
    );
  };

  const generateSubUsecaseList = () => {
    if (!enums || !enums?.usecase) {
      return null;
    }

    const usecases = Object.keys(enums?.usecase)
      ?.filter((el) => enums?.usecase[el]?.validSubUsecase === true)
      .map((item) => generateCheckbox(item, item, true));

    return usecases;
  };

  const generateDeviceTable = () => {
    if (!campaign) {
      return null;
    }

    const { devices } = campaign;

    let devicesList = (
      <TableRow key={null}>
        <TableCell colSpan={3}>{'No numbers found'}</TableCell>
      </TableRow>
    );

    if (devices && devices.length > 0) {
      devicesList = devices.map((device) => {
        return (
          <TableRow key={device.device_id}>
            <TableCell>
              <Link tabIndex={0} onClick={() => {}}>
                {device.reply_tn}
              </Link>
            </TableCell>
            <TableCell>{device.user_email}</TableCell>
            <TableCell>{device.device_name}</TableCell>
          </TableRow>
        );
      });
    }

    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 6 }}>
          Assigned Numbers
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Assigned User</TableCell>
              <TableCell>Device Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{devicesList}</TableBody>
        </Table>
      </>
    );
  };

  const generateCampaignInfo = () => {
    if (!campaign && isEditing) {
      return null;
    }

    return (
      <Stack gap={5}>
        <Grid container spacing={10} sx={{ width: '100%' }}>
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            <InputLabel
              sx={{
                width: 220,
              }}
            >
              Campaign Name
            </InputLabel>
            <TextField
              key={'campaign_name'}
              name={'campaign_name'}
              defaultValue={campaign?.campaign_name || ''}
              size="small"
              onChange={handleFormChange}
              sx={{ width: 240 }}
            />
          </Grid>
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            <InputLabel
              sx={{
                width: 220,
              }}
            >
              Campaign Status
            </InputLabel>
            <TextField
              key={'campaign_status'}
              name={'campaign_status'}
              defaultValue={campaign?.campaign_status || ''}
              size="small"
              onChange={handleFormChange}
              sx={{ width: 240 }}
              disabled={true}
            />
          </Grid>
          {campaign?.campaign_error ? (
            <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Campaign Error
              </InputLabel>
              <TextField
                key={'campaign_error'}
                name={'campaign_error'}
                defaultValue={campaign?.campaign_error || ''}
                size="small"
                onChange={handleFormChange}
                sx={{ width: 240 }}
                disabled={true}
                multiline
              />
            </Grid>
          ) : (
            <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Sharing Status
              </InputLabel>
              <TextField
                key={'sharing_status'}
                name={'sharing_status'}
                defaultValue={campaign?.sharing_status || ''}
                size="small"
                onChange={handleFormChange}
                sx={{ width: 240 }}
                disabled={true}
                multiline
              />
            </Grid>
          )}
        </Grid>

        {/* Top Section */}
        <Grid container spacing={10} sx={{ width: '100%' }}>
          {/* Left Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            <Grid size={12}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Vertical
              </InputLabel>
              {generateVerticalDropdown()}
            </Grid>
            <Grid>
              {generateCheckbox('Age Gated', 'age_gated')}
              {generateCheckbox('Auto Renewal', 'auto_renewal')}
              {generateCheckbox('Direct Lending', 'direct_lending')}
              {generateCheckbox('Embedded Link', 'embedded_link')}
              {generateCheckbox('Embedded Phone', 'embedded_phone')}
              {generateCheckbox(
                'Subscriber Help',
                'subscriber_help',
                false,
                true
              )}
              {generateCheckbox(
                'Subscriber Opt-In',
                'subscriber_opt_in',
                false,
                true
              )}
              {generateCheckbox(
                'Subscriber Opt-Out',
                'subscriber_opt_out',
                false,
                true
              )}
              {generateCheckbox('Number Pool', 'number_pool')}
            </Grid>
          </Grid>

          {/* Middle Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            <Grid size={12}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Use Cases
              </InputLabel>
              {generateUsecaseDropdown()}
            </Grid>
            <Grid>
              {' '}
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Sub-Use Cases
              </InputLabel>
              {generateSubUsecaseList()}
            </Grid>
          </Grid>

          {/* Right Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            <Grid size={12}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Sample 1
              </InputLabel>
              <TextField
                name="sample_1"
                defaultValue={campaign?.sample_1 || ''}
                onChange={handleFormChange}
                size="small"
                sx={{ width: 240, mb: 2 }}
              />
            </Grid>
            <Grid size={12}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Sample 2
              </InputLabel>
              <TextField
                name="sample_2"
                defaultValue={campaign?.sample_2 || ''}
                onChange={handleFormChange}
                size="small"
                sx={{ width: 240, mb: 2 }}
              />
            </Grid>
            <Grid size={12}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Sample 3
              </InputLabel>
              <TextField
                name="sample_3"
                defaultValue={campaign?.sample_3 || ''}
                onChange={handleFormChange}
                size="small"
                sx={{ width: 240, mb: 2 }}
              />
            </Grid>
            <Grid size={12}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Sample 4
              </InputLabel>
              <TextField
                name="sample_4"
                defaultValue={campaign?.sample_4 || ''}
                onChange={handleFormChange}
                size="small"
                sx={{ width: 240, mb: 2 }}
              />
            </Grid>
            <Grid size={12}>
              <InputLabel
                sx={{
                  width: 220,
                }}
              >
                Sample 5
              </InputLabel>
              <TextField
                name="sample_5"
                defaultValue={campaign?.sample_5 || ''}
                onChange={handleFormChange}
                size="small"
                sx={{ width: 240, mb: 2 }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* 2nd Section */}
        <Grid container spacing={10} sx={{ width: '100%' }}>
          {/* Left Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField(
              'Reference ID',
              'reference_id',
              false,
              undefined,
              isEditing
            )}
          </Grid>

          {/* Middle Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField('Message Flow', 'message_flow', true)}
          </Grid>

          {/* Right Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField('Description', 'description', true)}
          </Grid>
        </Grid>

        {/* 3rd Section */}
        <Grid container spacing={10} sx={{ width: '100%' }}>
          {/* Left Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField(
              'Terms of Service Link',
              'terms_and_conditions_link'
            )}
          </Grid>

          {/* Middle Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField('Privacy Policy Link', 'privacy_policy_link')}
          </Grid>

          {/* Right Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField('Affilliate Marketing', 'affiliate_marketing')}
          </Grid>
        </Grid>

        {/* 4th Section */}
        <Grid container spacing={10} sx={{ width: '100%' }}>
          {/* Left Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField(
              'Opt In Keywords',
              'opt_in_keywords',
              edits?.subscriber_opt_in
            )}
            {generateTextField(
              'Opt In Message',
              'opt_in_message',
              edits?.subscriber_opt_in,
              2
            )}
          </Grid>

          {/* Middle Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField(
              'Opt Out Keywords',
              'opt_out_keywords',
              edits?.subscriber_opt_out
            )}
            {generateTextField(
              'Opt Out Message',
              'opt_out_message',
              edits?.subscriber_opt_out,
              2
            )}
          </Grid>
          {/* Right Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField(
              'Help Keywords',
              'help_keywords',
              edits?.subscriber_help
            )}
            {generateTextField(
              'Help Message',
              'help_message',
              edits?.subscriber_help,
              2
            )}
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Grid container spacing={10} sx={{ width: '100%' }}>
          {/* Left Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField(
              'Embedded Link Sample',
              'embedded_link_sample',
              edits?.embedded_link,
              2
            )}
          </Grid>

          {/* Middle Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
            {generateTextField(
              'Embedded Phone Sample',
              'embedded_phone_sample',
              false,
              2,
              true
            )}
          </Grid>

          {/* Right Column */}
          <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}></Grid>
        </Grid>
      </Stack>
    );
  };

  const generateSaveButton = () => {
    let buttonText = <CircularProgress size={20} />;

    if (!loading) {
      buttonText = isEditing && !isResubmitting ? 'Save' : 'Submit';
    }

    return (
      <Button
        onClick={() => submitForm()}
        variant="contained"
        color="secondary"
        disabled={loading}
      >
        {buttonText}
      </Button>
    );
  };

  const generateTitle = () => {
    let title = `New Campaign for ${brandCode}`;
    if (isEditing) {
      title = `Campaign Information ${
        campaign?.campaign_code ? '(' + campaign?.campaign_code + ')' : ''
      }`;
    }
    return (
      <Stack direction="column" spacing={2}>
        {title}
        <Typography
          variant="caption"
          sx={{ fontSize: 14, textDecoration: 'underline' }}
          color="secondary"
        >
          {campaign?.brand_code && (
            <Link
              onClick={() =>
                navigate(`/admin/compliance/brand/${campaign.brand_id}`)
              }
            >
              {campaign.brand_code}
            </Link>
          )}
        </Typography>
      </Stack>
    );
  };

  return (
    <Layout
      title={generateTitle()}
      routes={AdminRoutes}
      accessLevel={accessLevel}
      accessType={accessType}
      backButtonText={'Back'}
      handleBackButton={() => navigate(-1)}
      titleRowRightComponent={<Box>{generateSaveButton()}</Box>}
    >
      {generateCampaignInfo()}
      {generateDeviceTable()}
    </Layout>
  );
};

export default CampaignViewPage;
